import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  serviceService,
  Service,
  getLocalizedString,
} from "../../services/firebaseService";
import DetailLayout from "../DetailLayout/DetailLayout";

export default function ServiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentLang = i18n.language || "en";

  useEffect(() => {
    if (!id) {
      navigate("/services");
      return;
    }

    const loadService = async () => {
      try {
        setLoading(true);
        const serviceData = await serviceService.getService(id);
        if (serviceData) {
          setService(serviceData);
          setError(null);
        } else {
          setError("Service not found");
        }
      } catch (err) {
        console.error("Error loading service:", err);
        setError("Failed to load service");
      } finally {
        setLoading(false);
      }
    };

    loadService();
  }, [id, navigate]);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    const loadService = async () => {
      try {
        const serviceData = await serviceService.getService(id!);
        if (serviceData) {
          setService(serviceData);
        } else {
          setError("Service not found");
        }
      } catch (err) {
        setError("Failed to load service");
      } finally {
        setLoading(false);
      }
    };
    loadService();
  };

  return (
    <DetailLayout
      loading={loading}
      error={error}
      notFoundTitle="Service Not Found"
      // notFoundMessage="The service you are looking for does not exist."
      backPath="/services"
      backLabel={t("common.backToServices")}
      onRetry={handleRetry}
    >
      {service &&
        (() => {
          const localizedTitle = getLocalizedString(service.title, currentLang);
          // const localizedShortDescription = getLocalizedString(
          //   service.shortDescription,
          //   currentLang
          // );
          const localizedDescription = getLocalizedString(
            service.description,
            currentLang
          );

          return (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 mb-12 items-start md:gap-8">
                <div className="w-full aspect-[4/3] rounded-xl overflow-hidden bg-[var(--background-secondary)] group md:aspect-[16/9]">
                  <img
                    src={service.image}
                    alt={localizedTitle}
                    className="w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "/images/placeholder.png";
                    }}
                  />
                </div>
                <div className="flex flex-col gap-4">
                  <h1 className="text-[2.5rem] font-extrabold text-[var(--text-primary)] leading-tight m-0 md:text-[2rem]">
                    {localizedTitle}
                  </h1>
                  {/* <h2 className="text-xl text-[var(--text-secondary)] leading-normal m-0 md:text-lg">
                    {localizedShortDescription}
                  </h2> */}
                  <div className="flex flex-wrap gap-4 items-center mt-4 md:flex-col md:items-start md:gap-3">
                    <span className="flex items-center gap-2 px-4 py-2 bg-[var(--background-secondary)] rounded-full text-lg font-bold text-primary">
                      ${service.price}
                    </span>
                    <span className="flex items-center gap-2 px-4 py-2 bg-[var(--gradient-secondary)]  rounded-full text-sm font-medium">
                      {service.duration}
                    </span>
                    <span className="flex items-center gap-2 px-4 py-2 bg-[var(--gradient-primary)] rounded-full text-sm font-medium">
                      {service.category}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid gap-8">
                <div className="bg-[var(--background-primary)] border border-[var(--border-color)] rounded-xl p-8 shadow-[0_2px_4px_var(--shadow-color)] md:p-6">
                  {/* <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2 md:text-xl">
                    About This Service
                  </h3> */}
                  <p className="text-[var(--text-secondary)] leading-relaxed mb-0">
                  <div
                    className="prose prose-lg max-w-none leading-relaxed
                      [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mt-12 [&_h1]:mb-6 [&_h1]:text-[var(--text-primary)]
                      [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:text-[var(--text-primary)]
                      [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-[var(--text-primary)]
                      [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:mt-6 [&_h4]:mb-3 [&_h4]:text-[var(--text-primary)]
                      [&_p]:my-6 [&_p]:text-[var(--text-secondary)] [&_p]:leading-relaxed [&_p]:text-base [&_p]:md:text-lg
                      [&_ul]:pl-6 [&_ul]:my-6 [&_ul]:list-disc [&_ul]:space-y-2
                      [&_ol]:pl-6 [&_ol]:my-6 [&_ol]:list-decimal [&_ol]:space-y-2
                      [&_li]:my-2 [&_li]:text-[var(--text-secondary)] [&_li]:leading-relaxed
                      [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--primary-color)] [&_blockquote]:pl-6 [&_blockquote]:pr-4 [&_blockquote]:italic [&_blockquote]:my-8 [&_blockquote]:text-[var(--text-secondary)] [&_blockquote]:bg-[var(--background-secondary)] [&_blockquote]:py-4 [&_blockquote]:rounded-r-lg [&_blockquote]:shadow-sm
                      [&_pre]:bg-[var(--background-secondary)] [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-6 [&_pre]:border [&_pre]:border-[var(--border-color)] [&_pre]:shadow-sm
                      [&_code]:bg-[var(--background-secondary)] [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_code]:text-[var(--primary-color)] [&_code]:border [&_code]:border-[var(--border-color)]
                      [&_a]:text-[var(--primary-color)] [&_a]:hover:text-[var(--primary-hover)] [&_a]:hover:underline [&_a]:font-medium [&_a]:transition-colors
                      [&_img]:rounded-xl [&_img]:my-8 [&_img]:shadow-lg [&_img]:w-full [&_img]:h-auto
                      [&_strong]:font-bold [&_strong]:text-[var(--text-primary)]
                      [&_em]:italic [&_em]:text-[var(--text-secondary)]
                      [&_table]:w-full [&_table]:my-6 [&_table]:border-collapse
                      [&_table_th]:border [&_table_th]:border-[var(--border-color)] [&_table_th]:px-4 [&_table_th]:py-2 [&_table_th]:bg-[var(--background-secondary)] [&_table_th]:text-left [&_table_th]:font-semibold
                      [&_table_td]:border [&_table_td]:border-[var(--border-color)] [&_table_td]:px-4 [&_table_td]:py-2 [&_table_td]:text-[var(--text-secondary)]
                      [&_hr]:my-8 [&_hr]:border-t [&_hr]:border-[var(--border-color)]
                      [&_canvas]:max-w-full [&_canvas]:h-auto [&_canvas]:rounded-lg [&_canvas]:border [&_canvas]:border-[var(--border-color)]
                      [&_iframe]:max-w-full [&_iframe]:rounded-lg [&_iframe]:my-6"
                    dangerouslySetInnerHTML={{ __html: localizedDescription }}
                  />
                  </p>
                </div>

                {service.features && service.features.length > 0 && (
                  <div className="bg-[var(--background-primary)] border border-[var(--border-color)] rounded-xl p-8 shadow-[0_2px_4px_var(--shadow-color)] md:p-6">
                    <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2 md:text-xl">
                      What's Included
                    </h3>
                    <ul className="list-none p-0 m-0">
                      {service.features.map((feature, index) => (
                        <li
                          key={index}
                          className="py-3 border-b border-[var(--border-color)] text-[var(--text-secondary)] relative pl-8 last:border-b-0 before:content-['✓'] before:absolute before:left-0 before:text-primary before:font-bold"
                        >
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="bg-[var(--gradient-primary)]  text-center py-12 px-8 rounded-xl mt-8">
                  <h3 className="text-2xl font-bold  mb-4">
                    Ready to Get Started?
                  </h3>
                  <p className=" mb-8">
                    Contact us to learn more about this service and how we can
                    help you achieve your goals.
                  </p>
                  <div className="flex gap-4 justify-center flex-wrap md:flex-col md:items-center">
                    <button
                      className="  border-none px-8 py-4 rounded-lg font-semibold cursor-pointer transition-all duration-200 hover:bg-[var(--background-secondary)] hover:-translate-y-0.5 hover:shadow-lg md:w-full md:max-w-[300px]"
                      onClick={() => navigate("/contact")}
                    >
                      Contact Us
                    </button>
                    <button
                      className="bg-transparent  border-2 border-white px-8 py-4 rounded-lg font-semibold cursor-pointer transition-all duration-200 hover:bg-white hover:text-primary md:w-full md:max-w-[300px]"
                      onClick={() => navigate("/services")}
                    >
                      View All Services
                    </button>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
    </DetailLayout>
  );
}
